import Vue from 'vue';
import moment from 'moment';

import { debounce } from 'debounce';
import { v4 as uuidv4 } from 'uuid';

import EventBus from '../EventsBus';
import {
  FormDefinition,
  ExistingSubmission,
  SubmissionVersion,
  VesselGlobal,
  FormSubmission,
  VesselConsumptionConfig,
  SharedGlobal,
} from './types/FormDefinition';
import { FieldDefinition, GlobalValue } from '../fields/types/FieldDefinition';
import { Field, FieldContext } from '../fields/field';
import { TanksField } from '../fields/field-types/TanksField';
import { ConsumptionField } from '../fields/field-types/ConsumptionField';
import { ConsumptionNumberField } from '../fields/field-types/ConsumptionNumberField';
import { ComputedField } from '../fields/field-types/ComputedField';
import { RepeatableField } from '../fields/field-types/RepeatableField';
import { DateTimeGMTField } from '../fields/field-types/DateTimeGMTField';
import { Computer } from '../formulas/Computer';
import { SubmissionGlobalValue } from '../formulas/types/ComputerDefinition';

// Some fields have a custom implementation that extends the base field class
const fieldTypeMap: {
  [key: string]: typeof Field | typeof TanksField;
} = {
  'storage-and-content': TanksField,
  consumption: ConsumptionField,
  'consumption-number': ConsumptionNumberField,
  computed: ComputedField,
  repeatable: RepeatableField,
  'datetime-gmt': DateTimeGMTField,
};

interface FormConstuctor {
  formDef: FormDefinition;
  sharedGlobals?: SharedGlobal[];
  globals?: SubmissionGlobalValue[];
  vesselGlobals?: VesselGlobal[];
  vesselConsumptionConfig?: VesselConsumptionConfig[];
  authorName?: string;
  vessel?: object;
  existingSubmission?: ExistingSubmission;
  submissionVersions?: SubmissionVersion[];
  onUpdate?(form: FormSubmission): void;
}

export class Form {
  public state: {
    fields: Field[];
    updatedGlobals: SubmissionGlobalValue[];
    latestVersion: SubmissionVersion | null;
    currentVersion: SubmissionVersion | null;
    formIsOpen: boolean;
    editable: boolean;
  };
  public formMeta: {
    id: string;
    date: string | Date;
    time: string;
    offset: string;
  };
  public formDef: FormDefinition;
  public computer: any;
  public globals: SubmissionGlobalValue[];
  public vesselGlobals: VesselGlobal[];
  public authorName?: string;
  public vessel?: object;
  public existingSubmission?: ExistingSubmission;
  public date?: string | Date;
  public previousSubmissionDate?: string | Date;
  public submissionVersions?: SubmissionVersion[];
  public fieldContext: FieldContext;
  private onUpdate?(): void;

  constructor({
    formDef,
    existingSubmission,
    sharedGlobals = [],
    globals = [],
    vesselGlobals = [],
    vesselConsumptionConfig = [],
    authorName = '',
    vessel = {},
    submissionVersions = [],
    onUpdate,
  }: FormConstuctor) {
    /**
     * Set primary values.
     */
    this.formDef = formDef;
    this.globals = globals;
    this.vesselGlobals = vesselGlobals;
    this.authorName = authorName;
    this.vessel = vessel;
    this.existingSubmission = existingSubmission;
    this.submissionVersions = submissionVersions;
    if (onUpdate) {
      this.onUpdate = debounce(() => {
        onUpdate(this.generateOutput());
      }, 500);
    }

    if (this.existingSubmission) {
      this.date = this.existingSubmission.reportDate;
    }

    this.formMeta = {
      id: this.existingSubmission?.id || uuidv4(),
      date: this.existingSubmission ? this.getDate() : '',
      time: this.existingSubmission ? this.getTime() : '',
      offset: this.existingSubmission ? this.getUTCOffset() : '',
    };

    /**
     * Initialise fields in this way to keep it referenced.
     */
    const fieldsInitialised: Field[] = [];

    /**
     * If we are editing submission there will already be global values. We need to
     * update the existing values.
     */
    const updatedGlobals = this.globals.filter(
      (globalValue) => globalValue.submissionId === this.formMeta.id,
    );

    this.computer = new Computer({
      fields: fieldsInitialised,
      globals: globals || [],
      vesselGlobals,
      updatedGlobals,
    });

    /**
     * If this is an existing submission, use the report date that
     * has been set to get the correct global values.
     */
    if (this.existingSubmission) {
      this.computer.setDate(this.existingSubmission.reportDate);
    }

    /**
     * Grab items from our computer as a reference to avoid circular dependencies for fields.
     */
    const fieldComputer = {
      getDependencies: (value) => this.computer.getDependencies(value),
      compute: (value) => this.computer.compute(value),
      globals: this.computer.globals,
    };

    this.fieldContext = {
      computer: fieldComputer,
      formDef: this.formDef,
      submissionGlobalValues: globals,
      sharedGlobals,
      initialisedFields: fieldsInitialised,
      vesselGlobals: this.vesselGlobals,
      vesselConsumptionConfig,
      updateGlobal: this.handleUpdatedGlobal.bind(this),
      updatedGlobals,
      createField: this.createNewField.bind(this),
      deleteField: this.deleteField.bind(this),
      existingSubmission: this.existingSubmission,
    };

    /**
     * Create new field objects for each field definition.
     */
    this.formDef.fields.forEach((fieldDef) => {
      const FieldClass = fieldTypeMap[fieldDef.type] || Field;
      fieldsInitialised.push(
        new FieldClass({
          fieldDef,
          context: this.fieldContext,
        }),
      );
    });

    /**
     * Create reactive state.
     */
    this.state = Vue.observable({
      fields: fieldsInitialised,
      updatedGlobals,
      editable: true,
      currentVersion: null,
      latestVersion: null,
      formIsOpen: false,
    });

    /**
     * If we have existing form data, set the content of each field with this and
     * open the form for viewing.
     */
    if (this.existingSubmission?.content) {
      this.setExistingContent(this.existingSubmission.content);
      this.setFormIsOpen(true);
    }

    /**
     * If the field has a `visibleIf` formula, run this on load.
     */
    this.state.fields.forEach((field) => {
      if (field.fieldDef?.formulas?.length) {
        const visibilityFormulas = field.fieldDef.formulas.filter((formula) => formula.includes('visibleIf'));
        const results = field.context.computer.compute(visibilityFormulas);

        results
          .filter((res) => res)
          .forEach((result) => {
            field[result.callback](result.value);
          });
      }
    });

    this.state.currentVersion = {
      version: this.existingSubmission?.version || 1,
      updated: this.formDef.updated,
      content: this.existingSubmission?.content || {},
    };

    this.state.latestVersion = { ...this.state.currentVersion };

    /**
     * Event listeners for methods to be called from within form components.
     */
    EventBus.$on('createNewField', (fieldDef) => {
      this.createNewField(fieldDef);
    });
    EventBus.$on('deleteField', (field) => {
      this.deleteField(field);
    });
    EventBus.$on('versionChanged', (version) => {
      this.handleVersionChanged(version);
    });

    if (this.existingSubmission) {
      const beforeConsumptionFields = this.state.fields.filter((field) => field.fieldDef.title?.includes('before consumption'));
      beforeConsumptionFields.forEach((field) => field.initialiseValue(
          this.existingSubmission?.reportDate!,
          this.globals,
      ));
    }
  }

  /**
   * Set wether the entire form is editable.
   */
  setEditable(canEdit: boolean): void {
    this.state.editable = canEdit;
    this.state.fields.forEach((field) => {
      field.setEditable(this.state.editable);
    });
  }

  /**
   * Get whether or not the form should be editable.
   */
  getEditable(): boolean {
    return this.state.editable;
  }

  /**
   * Set existing form content from an id referenced object
   * (i.e. set values and do not run computation methods).
   */
  setExistingContent(content: any): void {
    const existingContent = { ...content };

    Object.keys(existingContent).forEach((key) => {
      const fieldToUpdate = this.state.fields.find((field) => field.id === key);
      if (fieldToUpdate) {
        fieldToUpdate.setExistingContent(existingContent[key]);
      }
    });
  }

  /**
   * Handle an updated global value.
   */
  handleUpdatedGlobal(value: Omit<GlobalValue, 'id'>): void {
    if (!this.state) return;

    /**
     * Attempt to find a value with the same id as the new value in our updated globals.
     */
    const valueInUpdatedGlobals = this.state.updatedGlobals.find(
      (global) => global.globalId === value.globalId,
    );

    /**
     * If we can, just assign this the updated data.
     */
    if (valueInUpdatedGlobals) {
      valueInUpdatedGlobals.value = value.value;
    } else {
      /**
       * Otherwise create a new global value
       */
      const submissionGlobal: SubmissionGlobalValue = {
        id: uuidv4(),
        reportDate: this.date!,
        created: moment().toISOString(),
        updated: moment().toISOString(),
        deleted: false,
        globalId: value.globalId,
        value: value.value,
        submissionId: this.formMeta.id,
      };

      this.state.updatedGlobals.push(submissionGlobal);
    }

    EventBus.$emit(`${value.globalId}:updated:global`);
    if (this.onUpdate) {
      this.onUpdate();
    }
  }

  /**
   * Validate each and every field that the form contains.
   * This will also set every edited state to true to display this visually.
   */
  validate(): void {
    this.state.fields.forEach((field) => {
      field.setEdited(true);
      field.validate();
    });
  }

  /**
   * Set the Date for the form as an ISO (this is how we store it in the API).
   */
  setDate({
    date,
    time,
    offset,
  }: {
    date: string | Date;
    time: string;
    offset: string;
  }): string {
    this.formMeta.date = date;
    this.formMeta.time = time;
    this.formMeta.offset = offset;

    const parsedDate = moment
      .parseZone(`${date} ${time} ${offset}`, 'YYYY-MM-DD HH:mm Z')
      .toISOString(true);

    this.date = parsedDate;
    this.computer.setDate(parsedDate);

    EventBus.$emit('dateSet', parsedDate);
    this.state.fields.forEach((field) => field.initialiseValue(this.date!, this.globals));
    return this.date;
  }

  calculateSteamingHours() {
    this.state.fields.forEach((field) => {
      // Borrowed heavily from visibleIf formulas like that around line 215.
      const fieldDefinition = field.fieldDef;
      const hasAFormula = !!fieldDefinition.formulas?.length;
      // Only compute if there is a formula for the field and that formula contains a '#'
      if (hasAFormula && fieldDefinition.formulas![0].includes('#')) {
        const results = field.context.computer.compute(fieldDefinition.formulas);
        results
          .forEach((result) => {
            field[result.callback](result.value);
          });
      }
    });
  }
  /**
   * Return the date of the form as a string.
   */
  getDate(): string {
    return moment.parseZone(this.date).format('YYYY-MM-DD');
  }

  /**
   * Return the time of the form as a string.
   */
  getTime(): string {
    return moment.parseZone(this.date).format('HH:mm');
  }

  /**
   * Return the offset of the form as a string.
   */
  getUTCOffset(): string {
    return moment.parseZone(this.date).format('Z');
  }

  /**
   * Set the date of the submission that was closest prior to the
   * `current` submission date.
   */
  setPreviousSubmissionDate(value: string): void {
    this.previousSubmissionDate = value;
    this.computer.setPreviousSubmissionDate(value);
    this.calculateSteamingHours();
  }

  /**
   * Set whether the form is open and displaying fields from its schema.
   */
  setFormIsOpen(value: boolean): void {
    this.state.formIsOpen = value;
  }

  /**
   * Return a boolean based on whether any critical errors exist.
   */
  isValid(): boolean {
    this.validate();

    const fieldsWithCriticalErrors = this.state.fields.filter(
      (field) => field.getErrors().filter((error) => error.level === 'critical').length > 0,
    );

    if (fieldsWithCriticalErrors.length > 0) {
      console.error(fieldsWithCriticalErrors);
    }

    return fieldsWithCriticalErrors.length === 0;
  }

  /**
   * Create a new field using a FieldDefinition as input.
   */
  createNewField(fieldDef: FieldDefinition): Field {
    /**
     * Grab items from our computer as a reference to avoid circular dependencies for fields.
     */
    const newField = new Field({
      fieldDef,
      context: this.fieldContext,
    });
    const parentId = fieldDef?.parent?.length ? fieldDef.parent[0] : null;

    // TODO: Figure out if this is required
    if (parentId) {
      const parent = this.state.fields.find(
        (field) => field.fieldDef.id === parentId,
      );
      if (
        parent
        && parent.fieldDef.children
        && !parent.fieldDef.children.includes(fieldDef.id)
      ) {
        parent.fieldDef.children.push(fieldDef.id);
      }
    }

    this.state.fields.push(newField);
    return newField;
  }

  /**
   * Delete a field from the form.
   */
  deleteField(field: Field): void {
    const { fieldDef } = field;

    /**
     * If the field has a parent, remove the field id from its children list.
     */
    if (fieldDef.parent && fieldDef.parent[0]) {
      const parentId = fieldDef.parent[0] || '';
      const parentField = this.state.fields.find(
        (formField) => formField.fieldDef.id === parentId,
      );

      if (parentField?.fieldDef.children) {
        parentField.fieldDef.children.filter(
          (childId) => childId !== fieldDef.id,
        );
      }
    }

    /**
     * Remove the field from the forms state.
     */
    const fieldIndex = this.state.fields.findIndex(
      (formField) => formField.fieldDef.id === fieldDef.id,
    );

    this.state.fields.splice(fieldIndex, 1);
  }

  /**
   * Update the form version and show all the values associated
   * with this as a non-editable form (unless the version is the primary one).
   *
   * We will also update the date here accordingly and emit this for any
   * components that may be listening.
   *
   */
  handleVersionChanged(versionInstance: SubmissionVersion): void {
    this.state.currentVersion = versionInstance;
    const latestVersionNumber = this.state.latestVersion?.version;

    /**
     * We should only be able to edit the form if it is the primary version.
     */
    this.setEditable(versionInstance.version === latestVersionNumber);

    /**
     * If the primary form version has existing content, open the form.
     */
    if (!this.existingSubmission?.content) {
      this.setFormIsOpen(versionInstance.version !== latestVersionNumber);
    } else {
      this.setExistingContent(this.existingSubmission.content);
    }

    /**
     * Loop through any previous form versions we have and then set the content
     * and date from this.
     */
    if (this.submissionVersions?.length) {
      const previousSubmission = this.submissionVersions.find(
        (submission) => submission.version === versionInstance.version,
      );

      if (previousSubmission?.content) {
        this.setExistingContent(previousSubmission.content);
      }

      this.date = previousSubmission?.updated;
    }

    /**
     * If we are resetting the form to the primary version, use the current
     * date as the form date.
     */
    if (versionInstance.version === latestVersionNumber) {
      this.date = moment().format();
    }

    /**
     * Remove all fields that have been generated by repeatable instances.
     */
    this.state.fields
      .filter((field) => field.fieldDef.originalId)
      .forEach((field) => EventBus.$emit('deleteField', field));

    /**
     * Emit an event with the date of the form broken down for different fields.
     */
    EventBus.$emit('formDateUpdated', {
      date: this.getDate(),
      time: this.getTime(),
      offset:
        versionInstance.version === latestVersionNumber
          ? '+00:00'
          : this.getUTCOffset(),
    });

    /**
     * Set the edited state of all the fields to false to avoid errors showing on previous reports.
     * Also add generated fields (in repeatable instances) to be deleted.
     *
     * We should also now run `visibleIf` formulas for each field to display the new version
     * in the correct way depending on the values of each field.
     */
    this.state.fields.forEach((field) => {
      field.setEdited(false);

      if (field.fieldDef?.formulas?.length) {
        const visibilityFormulas = field.fieldDef.formulas.filter((formula) => formula.includes('visibleIf'));
        const results = field.context.computer.compute(visibilityFormulas);

        results.forEach((result) => {
          field[result.callback](result.value);
        });
      }
    });
  }

  /**
   * Generate API ready output from each field instance.
   */
  generateOutput(isDraft?: boolean): FormSubmission {
    /**
     * Get the content into a new object from each field.
     * example: { "08fec28581": "some value", ... }
     */
    const content = {};

    this.state.fields
      .filter((field) => !field.fieldDef.originalId)
      .forEach((field) => {
        content[field.id] = field.getContent();
      });

    /**
     * Loop through all repeatable instances and add their values to their original
     * field ids.
     */
    this.state.fields
      .filter((field) => field.fieldDef.originalId)
      .forEach((field) => {
        const originalFieldId = field.fieldDef.originalId || '';

        if (content[originalFieldId]) {
          if (Array.isArray(content[originalFieldId])) {
            content[originalFieldId].push(field.getContent());
          } else {
            content[originalFieldId] = [
              content[originalFieldId],
              field.getContent(),
            ];
          }
        } else {
          content[originalFieldId] = field.getContent();
        }
      });

    /**
     * Generate a new submission id, if we are editing an existing submission,
     * use that existing id.
     */
    let id = uuidv4();
    if (this.existingSubmission) {
      id = this.existingSubmission.id;
    }

    return {
      id,
      reportDate: this.date,
      authorName: this.authorName,
      vessel: { id: this.formDef.vesselId },
      title: this.formDef.title,
      status: isDraft ? 'draft' : 'complete',
      content,
      globalValues: this.state.updatedGlobals
        .map((updatedGlobal) => ({
          ...updatedGlobal,
          submissionId: id,
          reportDate: this.date,
          vesselId: this.formDef.vesselId,
        }))
        .filter((global) => global.globalId),
      form: {
        id: this.formDef.id,
        version: this.formDef.version,
      },
      type: this.formDef.type,
    };
  }
}
